import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@mui/material";
import { aplicarFiltroCatalogo } from "./filtroCatalogoDinamico.util";
import GenericDatePickerReference from "../GenericDatePickerReference";
import { dataParaMesIso, mesIsoParaData } from "./mesAnoFiltro.util";
import SelectFiltro, {
  BuscaFiltro,
  MultiSelectFiltro,
  FILTER_FIELD_SX,
} from "../../pages/Apuracoes/components/SelectFiltro";

import styles from "./FiltroCatalogoDinamico.module.css";

/** Botões no mesmo padrão dos filtros das telas de Apurações (ex.: ICMS, "Limpar filtros"). */
const BTN_BASE_SX = { borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40 };
const BTN_ADICIONAR_SX = { ...BTN_BASE_SX, bgcolor: "#18316e", px: 2, "&:hover": { bgcolor: "#152858" } };
const BTN_LIMPAR_SX = {
  ...BTN_BASE_SX,
  color: "#18316e",
  borderColor: "#18316e",
  "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" },
};

/**
 * Estado de um filtro dinâmico por catálogo de colunas: quais filtros estão ativos
 * (add/remove/update/clear) e a função `apply` para rodar sobre um array de linhas.
 * `add`/`remove`/`update`/`clear` são estáveis entre renders (useCallback com updates
 * funcionais) para poderem ser usadas como dependência de efeitos sem causar loops.
 * @param {Array<{id: string, label: string, type: string, options?: Array, test: Function}>} catalog
 */
export function useFiltroCatalogoDinamico(catalog) {
  const [ativos, setAtivos] = useState([]);

  const add = useCallback((id) => {
    if (!id) return;
    setAtivos((prev) =>
      prev.some((a) => a.id === id) ? prev : [...prev, { id, value: "", value2: "" }]
    );
  }, []);
  const remove = useCallback((id) => {
    setAtivos((prev) => prev.filter((a) => a.id !== id));
  }, []);
  const update = useCallback((id, patch) => {
    setAtivos((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }, []);
  const clear = useCallback(() => setAtivos([]), []);

  const apply = useMemo(() => {
    const snapshot = ativos;
    return (rows) => aplicarFiltroCatalogo(rows, snapshot, catalog);
  }, [ativos, catalog]);

  return { ativos, add, remove, update, clear, apply };
}

/** Seletor de mês/ano no mesmo contorno dos demais filtros do sistema (largura vem do wrapper). */
const MES_SX = { ...FILTER_FIELD_SX, width: "100%" };

/**
 * Campos que ocupam a largura toda da linha, com o nome no rótulo flutuante (sem texto à
 * esquerda). Vigência e período têm vários controles e faixa numérica tem dois campos sem
 * rótulo próprio, então mantêm o nome à esquerda.
 */
function ehCampoCheio(def) {
  return !["vigencia", "period", "range"].includes(def.type);
}

// até 4 linhas de 6 tags (cabem no card, ver .tagsLado); o resto vira "+N"
const MAX_TAGS = 24;

/** 5 primeiras + 5 últimas letras (o texto completo fica no tooltip) — só pra reconhecer a opção. */
const abreviar = (texto) => (texto.length <= 13 ? texto : `${texto.slice(0, 5)}...${texto.slice(-5)}`);

/** Tags das opções marcadas, à direita do campo (abaixo dele ficam os outros filtros); o × desmarca a opção. */
function TagsSelecao({ options, value, onRemove, onAltura }) {
  const marcadas = options.filter((o) => value.includes(o.value));
  const visivel = marcadas.length > 0 && marcadas.length < options.length;
  const conteudo = useRef(null);
  // informa a altura real das tags pra linha do filtro crescer junto (sem sobrepor o filtro de baixo)
  useEffect(() => {
    const el = conteudo.current;
    if (!visivel || !el || typeof ResizeObserver === "undefined") return undefined;
    const obs = new ResizeObserver(() => onAltura(el.offsetHeight));
    obs.observe(el);
    return () => {
      obs.disconnect();
      onAltura(0);
    };
  }, [visivel, onAltura]);
  if (!visivel) return null;
  return (
    <div className={styles.tagsLado}>
      <div ref={conteudo} className={styles.tagsConteudo}>
      {marcadas.slice(0, MAX_TAGS).map((o) => (
        <span key={o.value} className={styles.tag} title={o.label}>
          {abreviar(o.label)}
          <button
            type="button"
            className={styles.tagRemove}
            aria-label={`Remover ${o.label}`}
            onClick={() => onRemove(o.value)}
          >
            ×
          </button>
        </span>
      ))}
      {marcadas.length > MAX_TAGS ? (
        <span className={styles.tag} title={marcadas.slice(MAX_TAGS).map((o) => o.label).join(", ")}>
          +{marcadas.length - MAX_TAGS}
        </span>
      ) : null}
      </div>
    </div>
  );
}

function FiltroInput({ def, ativo, onUpdate }) {
  // Select = "um ou outro" (até 2 opções): não tem "Todos"; já nasce com a 1ª opção escolhida
  // (pra tirar o filtro, usa-se o × da linha).
  const [alturaTags, setAlturaTags] = useState(0);
  const primeira = def.type === "select" ? def.options?.[0]?.value : undefined;
  useEffect(() => {
    if (primeira !== undefined && !ativo.value) onUpdate({ value: primeira });
  }, [primeira, ativo.value, onUpdate]);

  if (def.type === "select") {
    return (
      <div className={styles.campoCheio}>
        <SelectFiltro
          label={def.label}
          ariaLabel={def.label}
          width="100%"
          value={ativo.value || ""}
          onChange={(value) => onUpdate({ value })}
          options={def.options || []}
        />
      </div>
    );
  }
  // Múltipla escolha com checkbox: o valor é a lista de opções marcadas (vazia = todas).
  if (def.type === "multiselect") {
    return (
      <div
        className={styles.campoCheio}
        // a linha cresce pra acomodar as linhas de tags (o campo continua com 40px)
        style={{ minHeight: alturaTags || undefined }}
      >
        <MultiSelectFiltro
          label={def.label}
          ariaLabel={def.label}
          value={Array.isArray(ativo.value) ? ativo.value : []}
          onChange={(value) => onUpdate({ value })}
          options={def.options || []}
        />
        <TagsSelecao
          options={def.options || []}
          value={Array.isArray(ativo.value) ? ativo.value : []}
          onRemove={(v) => onUpdate({ value: ativo.value.filter((x) => x !== v) })}
          onAltura={setAlturaTags}
        />
      </div>
    );
  }
  if (def.type === "range") {
    return (
      <div className={styles.rangeWrap}>
        <input
          type="number"
          className={styles.input}
          placeholder="Mín."
          value={ativo.value ?? ""}
          onChange={(e) => onUpdate({ value: e.target.value })}
          aria-label={`${def.label} mínimo`}
        />
        <span className={styles.rangeSep}>até</span>
        <input
          type="number"
          className={styles.input}
          placeholder="Máx."
          value={ativo.value2 ?? ""}
          onChange={(e) => onUpdate({ value2: e.target.value })}
          aria-label={`${def.label} máximo`}
        />
      </div>
    );
  }
  if (def.type === "vigencia") {
    const modos = def.modos || [];
    const aviso = def.aviso ? def.aviso(ativo) : null;
    // Seletor de data do sistema (calendário só com mês e ano, MM/AAAA). O filtro guarda o mês
    // como "YYYY-MM"; data incompleta vira marcador de inválido e o aviso explica o formato.
    const mes = (campo, rotulo, sufixo) => (
      <div className={styles.vigenciaMes}>
        <GenericDatePickerReference
          label={rotulo}
          ariaLabel={`${def.label} ${sufixo}`}
          value={mesIsoParaData(ativo[campo])}
          onChange={(data) => onUpdate({ [campo]: dataParaMesIso(data) })}
          sx={MES_SX}
        />
      </div>
    );
    return (
      <div className={styles.vigenciaWrap}>
        {mes("value", "De", "de")}
        {mes("value2", "Até", "até")}
        <SelectFiltro
          label="Regra"
          ariaLabel={`${def.label} regra`}
          width="130px"
          value={ativo.modo || (modos[0] && modos[0].value) || ""}
          onChange={(modo) => onUpdate({ modo: modo || (modos[0] && modos[0].value) })}
          options={modos}
        />
        {aviso ? (
          <span className={styles.vigenciaAviso} role="alert">
            {aviso}
          </span>
        ) : null}
      </div>
    );
  }
  if (def.type === "period") {
    return (
      <div className={styles.rangeWrap}>
        <input
          type="month"
          className={styles.input}
          value={ativo.value || ""}
          onChange={(e) => onUpdate({ value: e.target.value })}
          aria-label={`${def.label} início`}
        />
        <span className={styles.rangeSep}>até</span>
        <input
          type="month"
          className={styles.input}
          value={ativo.value2 || ""}
          onChange={(e) => onUpdate({ value2: e.target.value })}
          aria-label={`${def.label} fim`}
        />
      </div>
    );
  }
  return (
    <div className={styles.campoCheio}>
      <BuscaFiltro
        label={def.label}
        ariaLabel={def.label}
        width="100%"
        placeholder={def.placeholder || ""}
        value={ativo.value || ""}
        onChange={(value) => onUpdate({ value })}
      />
    </div>
  );
}

/**
 * UI do filtro dinâmico: select "Adicionar filtro" + chips removíveis por filtro ativo.
 * Estado controlado externamente por `useFiltroCatalogoDinamico`.
 */
export default function FiltroCatalogoDinamico({ catalog, ativos, onAdd, onRemove, onUpdate, onClear }) {
  const [selecionado, setSelecionado] = useState("");

  const usados = useMemo(() => new Set(ativos.map((a) => a.id)), [ativos]);

  // Todos os campos ficam na lista; os que já têm filtro ativo ganham "(em uso)". O campo
  // continua mostrando o último escolhido depois do "+ Adicionar", para o usuário saber
  // qual filtro está sendo aplicado.
  const opcoesCampo = useMemo(
    () => [
      { value: "", label: "Selecione um campo…" },
      ...catalog.map((c) => ({
        value: c.id,
        label: usados.has(c.id) ? `${c.label} (em uso)` : c.label,
      })),
    ],
    [catalog, usados]
  );

  const jaEmUso = usados.has(selecionado);

  const handleAdd = () => {
    if (!selecionado || jaEmUso) return;
    onAdd(selecionado);
  };

  const handleLimpar = () => {
    onClear();
    setSelecionado("");
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.addRow}>
        <span className={styles.title}>Filtros</span>
        {/* Largura própria: com 220px o "(em uso)" ficava cortado ("Período de vigência…"). */}
        <div className={styles.campoFiltro}>
          <SelectFiltro
            label="Adicionar filtro"
            ariaLabel="Campo de filtro"
            width="100%"
            value={selecionado}
            onChange={setSelecionado}
            options={opcoesCampo}
          />
        </div>
        <Button
          variant="contained"
          size="small"
          disabled={!selecionado || jaEmUso}
          onClick={handleAdd}
          sx={BTN_ADICIONAR_SX}
        >
          + Adicionar
        </Button>
        <Button
          variant="outlined"
          size="small"
          disabled={!ativos.length}
          onClick={handleLimpar}
          sx={BTN_LIMPAR_SX}
        >
          Limpar filtros
        </Button>
      </div>
      <div className={styles.ativosList}>
        {!ativos.length ? (
          <p className={styles.vazio}>
            Nenhum filtro ativo. Use “Adicionar filtro” para refinar a busca.
          </p>
        ) : (
          ativos.map((af) => {
            const def = catalog.find((c) => c.id === af.id);
            if (!def) return null;
            return (
              <div
                key={af.id}
                className={`${styles.ativoRow}${def.type === "vigencia" ? ` ${styles.ativoRowVigencia}` : ""}`}
              >
                {ehCampoCheio(def) ? null : <span className={styles.ativoLabel}>{def.label}</span>}
                <FiltroInput def={def} ativo={af} onUpdate={(patch) => onUpdate(af.id, patch)} />
                <button
                  type="button"
                  className={styles.ativoRemove}
                  aria-label={`Remover filtro ${def.label}`}
                  onClick={() => onRemove(af.id)}
                >
                  ×
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
